/**
 * Watching a device, once its owner has agreed.
 *
 * Built because five builds went out chasing a walk that would not register a checkpoint, and each
 * round cost a drive and a walk to learn one fact. What is missing in that loop is not cleverness,
 * it is sight: whether a GPS fix arrived, what the engine made of it, and which call the server
 * refused. The device has always known; it had no way to say.
 *
 * The feed is capped. Nothing in this app is virtualised, and an hour of GPS at 1 Hz would make the
 * viewer the performance problem it was built to diagnose.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Link,
  TextField,
  Typography,
} from '@mui/material'

import { PageShell } from '@components/common/PageShell'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { diagnosticsApi, type DiagnosticsGrant } from '../../../api/endpoints/diagnostics'
import { REMOTE_ROUTES } from '../remoteRoutes'
import { signalRService } from '../../../services/signalr.service'
import { tokenStorage } from '../../../services/tokenStorage'

/** Rows kept on screen. Old ones fall off the top. */
const MAX_ROWS = 400

/**
 * Frames kept on screen.
 *
 * Few on purpose. These are pictures of somebody's screen held in a browser tab, and a scrollback
 * of them would outlive the reason any single one was taken.
 */
const MAX_FRAMES = 6

/** How long the capture button waits before assuming the device is not going to answer. */
const CAPTURE_TIMEOUT_MS = 15000

/** One line of the conversation. `from` is the server's, read off the sender's connection role. */
interface ChatLine {
  id: string
  from: 'watcher' | 'subject'
  text: string
  at: string
}

/**
 * Openers worth a click.
 *
 * Support sessions start the same three ways, and typing them out forty times a day is how a
 * watcher ends up reaching for the control pad instead of asking.
 */
const CHAT_OPENERS = [
  'Hi — what are you seeing on screen right now?',
  'Could you go back to the previous screen and try again?',
  'Thanks, that is what I needed. You can stop sharing whenever you like.',
]

interface FeedRow {
  key: string
  at: string
  kind: string
  detail: string
  /** Recorded before this watcher attached, and replayed on join. */
  replay: boolean
}

export default function DiagnosticsPage() {
  const [grants, setGrants] = useState<DiagnosticsGrant[]>([])
  const [loading, setLoading] = useState(true)
  /** The session list itself failed; shown in place of the list, with Retry. */
  const [listError, setListError] = useState<string | null>(null)
  /** Anything an action refused (watch, capture, send, end). */
  const [error, setError] = useState<string | null>(null)
  /** The session waiting on "End this session?" and whether the revoke is in flight. */
  const [endTarget, setEndTarget] = useState<DiagnosticsGrant | null>(null)
  const [ending, setEnding] = useState(false)

  const [watching, setWatching] = useState<string | null>(null)
  /**
   * Which session is mid-connect.
   *
   * Attaching takes a few seconds — two negotiate hops and a socket — and with nothing on screen
   * during it the button reads as broken, which invites a second click that tears the first
   * attempt down and starts again.
   */
  const [connecting, setConnecting] = useState<string | null>(null)
  const [loadingRecent, setLoadingRecent] = useState<string | null>(null)
  /** The feed is shown while watching, and also after a read-back with nothing attached. */
  const [showFeed, setShowFeed] = useState(false)
  const [rows, setRows] = useState<FeedRow[]>([])
  const [subjectOnline, setSubjectOnline] = useState(false)
  const rowSeq = useRef(0)

  /**
   * The grant being watched, not just its id.
   *
   * The capture and control panels need to know what the session was allowed to do, and the list
   * above is reloaded every ten seconds — reading the powers out of it would make the panels blink
   * out and back on every poll.
   */
  const [watchedGrant, setWatchedGrant] = useState<DiagnosticsGrant | null>(null)

  /** Newest first. Kept small: these are pictures of somebody's screen, not a gallery. */
  const [frames, setFrames] = useState<Array<{ url: string; at: string }>>([])
  const [capturing, setCapturing] = useState(false)

  /**
   * The conversation, and whether the hub is carrying one.
   *
   * `chatEnabled` is a server switch rather than a grant power, so it arrives on join in
   * DiagnosticsReady rather than being read off the session like canCaptureScreen. Reading it off
   * the grant would offer a composer whose every send came back refused.
   */
  const [chatEnabled, setChatEnabled] = useState(false)
  const [chat, setChat] = useState<ChatLine[]>([])
  const [draft, setDraft] = useState('')
  const chatEnd = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    try {
      setGrants(await diagnosticsApi.issued())
      setListError(null)
    } catch {
      setListError('Could not load diagnostics sessions.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
    // Invites are accepted on a phone, so the answer arrives while this page is idle.
    const timer = setInterval(() => void load(), 10000)
    return () => clearInterval(timer)
  }, [load])

  /**
   * End a session for good. Confirmed first: it revokes what the person agreed to and deletes
   * the frames, and asking them again means another round trip to their phone. A failure is
   * shown instead of swallowed, which used to look exactly like a session that ended.
   */
  const stop = async (grantId: string) => {
    setEnding(true)
    try {
      await diagnosticsApi.revoke(grantId)
      if (watching === grantId) await unwatch()
      setEndTarget(null)
      await load()
    } catch (err) {
      setEndTarget(null)
      setError(`Could not end that session: ${err instanceof Error ? err.message : 'refused'}`)
    } finally {
      setEnding(false)
    }
  }

  const unwatch = useCallback(async () => {
    await signalRService.stopDiagnosticsConnection()
    setWatching(null)
    setWatchedGrant(null)
    setSubjectOnline(false)
    // Frames go with the session. They are deleted server-side when it ends, and leaving them on
    // screen afterwards would show pictures the person has already stopped sharing.
    setFrames([])
    setChat([])
    setChatEnabled(false)
    setCapturing(false)
  }, [])

  /**
   * Show what the server already holds, without connecting to anything.
   *
   * Watching is the live path and this is not a substitute for it, but reading back events the
   * device recorded ten minutes ago should not depend on a websocket agreeing to open. When the
   * hub will not connect, this is the difference between having the answer and not.
   */
  const loadRecent = async (grant: DiagnosticsGrant) => {
    setLoadingRecent(grant.id)
    setError(null)
    try {
      const batches = await diagnosticsApi.recent(grant.id)
      const parsed = batches.flatMap((batch) => {
        try {
          return (
            JSON.parse(batch.events) as Array<{ t: number; kind: string; data: Record<string, unknown> }>
          ).map((event) => ({
            key: `${event.t}-${rowSeq.current++}`,
            at: new Date(event.t).toLocaleTimeString(),
            kind: event.kind,
            detail: describe(event.kind, event.data),
            replay: true,
          }))
        } catch {
          return []
        }
      })
      // An empty read-back is an answer, not a failure: the feed shows a neutral "nothing kept"
      // line instead of a red alert.
      setRows(parsed.slice(-MAX_ROWS))
      setShowFeed(true)
    } catch {
      setError('Could not read that session back.')
    } finally {
      setLoadingRecent(null)
    }
  }

  const watch = async (grant: DiagnosticsGrant) => {
    const token = tokenStorage.getAccessToken()
    if (!token) {
      // This used to return silently, so an expired session looked exactly like a dead button:
      // no feed, no error, nothing in the console, and no request on the wire to explain it.
      setError('Your sign-in has expired here. Reload the page and sign in again to watch.')
      return
    }

    // A second click while the first is still connecting would stop the connection it is waiting on.
    if (connecting) return

    setConnecting(grant.id)
    try {
      await connect(grant, token)
    } finally {
      setConnecting(null)
    }
  }

  const connect = async (grant: DiagnosticsGrant, token: string) => {
    await signalRService.stopDiagnosticsConnection()
    setRows([])

    let connection
    try {
      connection = await signalRService.startDiagnosticsConnection(token)
    } catch (err) {
      // Separated from the join failure below, because the two mean different things and a single
      // "could not join" message hid a hub host that did not resolve.
      setError(`Could not reach the diagnostics hub: ${err instanceof Error ? err.message : 'unknown error'}`)
      return
    }

    try {

      connection.on('DiagnosticsBatch', (payload: { events?: string; replay?: boolean }) => {
        let parsed: Array<{ t: number; kind: string; data: Record<string, unknown> }> = []
        try {
          parsed = JSON.parse(payload?.events ?? '[]')
        } catch {
          return
        }

        setRows((current) => {
          const next = [
            ...current,
            ...parsed.map((event) => ({
              key: `${event.t}-${rowSeq.current++}`,
              at: new Date(event.t).toLocaleTimeString(),
              kind: event.kind,
              detail: describe(event.kind, event.data),
              replay: !!payload?.replay,
            })),
          ]
          // Cap by dropping the oldest — the recent past is what a live watch is for.
          return next.length > MAX_ROWS ? next.slice(next.length - MAX_ROWS) : next
        })
      })

      connection.on('DiagnosticsPresence', (payload: { role?: string; joined?: boolean }) => {
        if (payload?.role === 'subject') setSubjectOnline(!!payload.joined)
      })

      connection.on('DiagnosticsError', (message: string) => {
        setError(message)
        // A refused capture leaves the button spinning otherwise, and the refusal is the only
        // thing that ever comes back — the device never answers a request it was not given.
        setCapturing(false)
      })

      connection.on('DiagnosticsFrame', (payload: { url?: string; capturedAt?: string }) => {
        if (!payload?.url) return
        setCapturing(false)
        setFrames((current) =>
          [
            { url: payload.url as string, at: new Date(payload.capturedAt ?? Date.now()).toLocaleTimeString() },
            ...current,
          ].slice(0, MAX_FRAMES)
        )
      })

      connection.on('DiagnosticsReady', (payload: { chatEnabled?: boolean }) => {
        setChatEnabled(!!payload?.chatEnabled)
      })

      connection.on(
        'DiagnosticsChat',
        (payload: { messageId?: string; from?: string; text?: string; sentAt?: string }) => {
          if (!payload?.messageId || !payload.text) return
          // Dropped if already held: the hub replays the conversation on join, and a watcher who
          // reconnects would otherwise see every line twice.
          setChat((current) =>
            current.some((line) => line.id === payload.messageId)
              ? current
              : [
                  ...current,
                  {
                    id: payload.messageId as string,
                    from: payload.from === 'watcher' ? 'watcher' : 'subject',
                    text: payload.text as string,
                    at: new Date(payload.sentAt ?? Date.now()).toLocaleTimeString(),
                  },
                ]
          )
        }
      )

      await connection.invoke('JoinAsWatcher', grant.id)
      setWatching(grant.id)
      setWatchedGrant(grant)
      setShowFeed(true)
    } catch (err) {
      // A refusal from the hub also arrives on DiagnosticsError with the server's own wording; this
      // covers the invoke itself failing.
      setError(`Could not join that session: ${err instanceof Error ? err.message : 'refused'}`)
      await unwatch()
    }
  }

  /**
   * Ask the device for a picture.
   *
   * Fire and forget: the hub relays the request and the frame comes back over HTTP a moment later
   * as a DiagnosticsFrame. There is no correlation id to wait on, so the button clears itself when
   * a frame or an error arrives, and on a timer if neither does.
   */
  const capture = async () => {
    const connection = signalRService.getDiagnosticsConnection()
    if (!connection || !watching) return

    setCapturing(true)
    try {
      await connection.invoke('RequestFrame', watching)
    } catch (err) {
      setError(`Could not ask for a capture: ${err instanceof Error ? err.message : 'refused'}`)
      setCapturing(false)
      return
    }

    // A phone that is asleep never answers. Without this the button stays spinning and reads as a
    // broken control rather than an unanswered request.
    window.setTimeout(() => setCapturing(false), CAPTURE_TIMEOUT_MS)
  }

  /** Send one command. The device decides what the names in it mean; see remoteTargets there. */
  const send = async (command: Record<string, unknown>) => {
    const connection = signalRService.getDiagnosticsConnection()
    if (!connection || !watching) return

    try {
      await connection.invoke('SendCommand', watching, JSON.stringify(command))
    } catch (err) {
      setError(`Could not send that: ${err instanceof Error ? err.message : 'refused'}`)
    }
  }

  /**
   * Say something to the device.
   *
   * The id is made here so the line can be shown at once and still be recognised when the hub
   * replays it on a reconnect. Shown before it is sent, and left in place if the send fails, for
   * the same reason the phone does it: a message that vanishes is worse than one marked as unsent.
   */
  const say = async (text: string) => {
    const connection = signalRService.getDiagnosticsConnection()
    const trimmed = text.trim()
    if (!connection || !watching || !trimmed) return

    const id = crypto.randomUUID()
    setChat((current) => [
      ...current,
      { id, from: 'watcher', text: trimmed, at: new Date().toLocaleTimeString() },
    ])
    setDraft('')

    try {
      await connection.invoke('SendChat', watching, trimmed, id)
    } catch (err) {
      setError(`Could not send that message: ${err instanceof Error ? err.message : 'refused'}`)
    }
  }

  // Newest line in view without scrolling the page it sits on.
  useEffect(() => {
    chatEnd.current?.scrollIntoView({ block: 'nearest' })
  }, [chat.length])

  useEffect(() => () => void signalRService.stopDiagnosticsConnection(), [])

  return (
    <PageShell
      title="Device diagnostics"
      subtitle={
        <>
          Sessions people have agreed to. Ask for one from a user's panel on the{' '}
          <Link component={RouterLink} to="/users">Users</Link> page; the person is already in front
          of you there.
        </>
      }
    >

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Your sessions
      </Typography>

      {loading ? (
        <CircularProgress size={22} />
      ) : listError && grants.length === 0 ? (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={() => void load()}>
              Retry
            </Button>
          }
        >
          {listError}
        </Alert>
      ) : grants.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Nothing open. Ask from a user's panel on the Users page; the request appears here until
          it is answered.
        </Typography>
      ) : (
        <Stack spacing={1} sx={{ mb: 3 }}>
          {/* A background poll failing after the list loaded keeps the list, with a note */}
          {listError && (
            <Alert severity="warning">{listError} Showing the last list that loaded.</Alert>
          )}
          {grants.map((grant) => (
            <Paper
              key={grant.id}
              sx={{
                p: 1.5,
                display: 'flex',
                // Stacks on a phone: status + who on top, buttons underneath at full width
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1, sm: 2 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flex: 1, minWidth: 0 }}>
                <Chip
                  size="small"
                  label={grant.status}
                  color={grant.isStreamable ? 'success' : 'default'}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Name links to the user's page; the id stands in when the account has no
                      name (or is gone) so the row is never blank. */}
                  <Link
                    component={RouterLink}
                    to={`/users/${grant.subjectUserId}`}
                    variant="body2"
                    sx={{
                      fontFamily: grant.subjectDisplayName ? undefined : 'monospace',
                      overflowWrap: 'anywhere',
                      display: 'block',
                    }}
                  >
                    {grant.subjectDisplayName || grant.subjectUserId}
                  </Link>
                  {grant.subjectEmail && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                      sx={{ overflowWrap: 'anywhere' }}
                    >
                      {grant.subjectEmail}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" component="div">
                    {grant.reason || 'No reason given'} · expires{' '}
                    {new Date(grant.expiresAt).toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}{' '}
                    · {grant.eventCount} events
                    {grant.frameCount > 0 && ` · ${grant.frameCount} frames`}
                    {grant.commandCount > 0 && ` · ${grant.commandCount} commands`}
                  </Typography>
                  {/* What they agreed to, on the row rather than hidden behind Watch. Somebody
                      scanning this list should be able to see which sessions carry the larger
                      powers without opening each one. */}
                  {(grant.allowsScreen || grant.allowsControl) && (
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                      {grant.allowsScreen && <Chip size="small" variant="outlined" label="screen" />}
                      {grant.allowsControl && (
                        <Chip size="small" variant="outlined" color="warning" label="control" />
                      )}
                    </Stack>
                  )}
                </Box>
              </Box>
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{
                  flexWrap: 'wrap',
                  flexShrink: 0,
                  '& > *': { flex: { xs: '1 1 auto', sm: '0 0 auto' }, minHeight: { xs: 40, sm: 'auto' } },
                }}
              >
                <Button
                  size="small"
                  variant={watching === grant.id ? 'outlined' : 'contained'}
                  disabled={!grant.isStreamable || connecting !== null}
                  onClick={() => (watching === grant.id ? void unwatch() : void watch(grant))}
                >
                  {connecting === grant.id
                    ? 'Connecting…'
                    : watching === grant.id
                      ? 'Stop watching'
                      : 'Watch'}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={loadingRecent !== null}
                  onClick={() => void loadRecent(grant)}
                >
                  {loadingRecent === grant.id ? 'Loading…' : 'Show recent'}
                </Button>
                <Button size="small" color="error" variant="outlined" onClick={() => setEndTarget(grant)}>
                  End
                </Button>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {watching && chatEnabled && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} useFlexGap sx={{ mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2">Messages</Typography>
            <Typography variant="caption" color="text.secondary">
              Shown in the bar on their device, where they can write back. Ask before you drive —
              being told what to press is less alarming than watching the screen move on its own.
            </Typography>
          </Stack>

          <Box
            sx={{
              // Viewport-relative on a phone so the thread and the composer fit one screen
              maxHeight: { xs: '35vh', md: 260 },
              overflowY: 'auto',
              mb: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
            }}
          >
            {chat.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                Nothing said yet. A sleeping phone will not see a message until it is opened —
                it is delivered when the app comes back.
              </Typography>
            ) : (
              chat.map((line) => (
                <Box
                  key={line.id}
                  sx={{
                    alignSelf: line.from === 'watcher' ? 'flex-end' : 'flex-start',
                    maxWidth: '80%',
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 1.5,
                    bgcolor: line.from === 'watcher' ? 'primary.main' : 'action.hover',
                    color: line.from === 'watcher' ? 'primary.contrastText' : 'text.primary',
                  }}
                >
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{line.text}</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    {line.from === 'watcher' ? 'You' : 'Them'} · {line.at}
                  </Typography>
                </Box>
              ))
            )}
            <div ref={chatEnd} />
          </Box>

          {/* Full text, wrapping: a reply cut at 34 characters could not be read before sending */}
          <Stack direction="row" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
            {CHAT_OPENERS.map((opener) => (
              <Button
                key={opener}
                size="small"
                variant="outlined"
                onClick={() => void say(opener)}
                sx={{
                  textTransform: 'none',
                  whiteSpace: 'normal',
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                  flex: { xs: '1 1 100%', sm: '0 1 auto' },
                  minHeight: 40,
                }}
              >
                {opener}
              </Button>
            ))}
          </Stack>

          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              fullWidth
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  void say(draft)
                }
              }}
              placeholder="Write to them…"
              // Matches the hub's cap, so a long message is stopped here rather than refused there.
              inputProps={{ maxLength: 1000 }}
            />
            <Button variant="contained" disabled={!draft.trim()} onClick={() => void say(draft)}>
              Send
            </Button>
          </Stack>
        </Paper>
      )}

      {watching && watchedGrant?.canCaptureScreen && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} useFlexGap sx={{ mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2">Screen</Typography>
            <Button size="small" variant="contained" disabled={capturing} onClick={() => void capture()}>
              {capturing ? 'Asking…' : 'Capture'}
            </Button>
            <Typography variant="caption" color="text.secondary">
              One picture per press. The device sends its own view — not the notification shade or
              anything else on their phone — and these are deleted when the session ends.
            </Typography>
          </Stack>

          {frames.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              Nothing captured yet. A sleeping phone will not answer until it is opened.
            </Typography>
          ) : (
            <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1 }}>
              {frames.map((frame) => (
                <Box key={frame.url} sx={{ flexShrink: 0, textAlign: 'center' }}>
                  {/* Opens full size in a tab rather than a lightbox: reading a screenshot of a
                      phone in a strip this size is not the point, and the browser already has a
                      perfectly good image viewer. */}
                  <a href={frame.url} target="_blank" rel="noreferrer">
                    <Box
                      component="img"
                      src={frame.url}
                      alt={`Screen at ${frame.at}`}
                      sx={{
                        height: { xs: 180, sm: 220 },
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        display: 'block',
                      }}
                    />
                  </a>
                  <Typography variant="caption" color="text.secondary">
                    {frame.at}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Paper>
      )}

      {watching && watchedGrant?.canControl && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} useFlexGap sx={{ mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2">Control</Typography>
            <Typography variant="caption" color="text.secondary">
              Their bar says so while you do this, and every command is in the feed below. There is
              no tap — the app cannot be given one — so this scrolls and moves between screens.
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Button size="small" variant="outlined" onClick={() => void send({ action: 'scroll', delta: -400 })}>
              Scroll up
            </Button>
            <Button size="small" variant="outlined" onClick={() => void send({ action: 'scroll', delta: 400 })}>
              Scroll down
            </Button>
            <Button size="small" variant="outlined" onClick={() => void send({ action: 'scrollTop' })}>
              Top
            </Button>
            <Button size="small" variant="outlined" onClick={() => void send({ action: 'back' })}>
              Back
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {REMOTE_ROUTES.map((route) => (
              <Button
                key={route.name}
                size="small"
                onClick={() => void send({ action: 'navigate', target: route.name })}
              >
                {route.label}
              </Button>
            ))}
          </Stack>
        </Paper>
      )}

      {(watching || showFeed) && (
        <>
          <Stack direction="row" alignItems="center" spacing={1} useFlexGap sx={{ mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2">{watching ? 'Live feed' : 'Kept by the server'}</Typography>
            {watching && (
              <Chip
                size="small"
                label={subjectOnline ? 'device connected' : 'device not connected'}
                color={subjectOnline ? 'success' : 'warning'}
              />
            )}
            <Typography variant="caption" color="text.secondary">
              {/*
                This used to describe the phone only, and read as a promise about the whole path:
                the device did catch up, and the server then dropped the catch-up unless somebody
                happened to be watching at that instant. It is kept now, so the sentence is true.
              */}
              A backgrounded phone cannot hold a socket open — it keeps recording and catches up
              when the app is opened. Anything from the last few minutes is shown when you attach,
              marked <em>earlier</em>.
            </Typography>
          </Stack>

          <Paper
            variant="outlined"
            sx={{
              p: 1,
              maxHeight: { xs: '60vh', md: 480 },
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: 12,
              bgcolor: 'background.default',
            }}
          >
            {rows.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                {watching
                  ? 'Waiting for the device… it reports in every twenty seconds even when idle.'
                  : 'Nothing kept for that session yet. The device has not published since it was accepted.'}
              </Typography>
            ) : (
              rows.map((row) => (
                <Box
                  key={row.key}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    py: 0.25,
                    color:
                      row.kind === 'ack' || row.kind === 'http'
                        ? 'error.main'
                        : row.kind === 'command'
                          ? 'warning.main'
                        : row.kind === 'map' && row.detail.includes('SLOW')
                          ? 'warning.main'
                        : row.kind === 'dropped'
                          ? 'warning.main'
                          : row.kind === 'screen'
                            ? 'info.main'
                            : 'inherit',
                  }}
                >
                  <span style={{ opacity: 0.6, flexShrink: 0 }}>{row.at}</span>
                  {/* Otherwise a replayed burst reads as a device that just did all of this at once. */}
                  {row.replay && (
                    <span style={{ opacity: 0.5, flexShrink: 0, fontStyle: 'italic' }}>earlier</span>
                  )}
                  <span style={{ fontWeight: 700, flexShrink: 0, minWidth: 56 }}>{row.kind}</span>
                  <span style={{ wordBreak: 'break-word' }}>{row.detail}</span>
                </Box>
              ))
            )}
          </Paper>
        </>
      )}

      <ConfirmDialog
        open={endTarget !== null}
        title="End this session?"
        message="Watching stops, the person's agreement is revoked and any screen captures are deleted. To look again you will have to ask them again."
        confirmLabel="End session"
        destructive
        loading={ending}
        onConfirm={() => endTarget && void stop(endTarget.id)}
        onClose={() => !ending && setEndTarget(null)}
      />
    </PageShell>
  )
}

/** One readable line per event. The feed is read at a glance, not parsed. */
function describe(kind: string, data: Record<string, unknown>): string {
  if (kind === 'fix') {
    const sections = Array.isArray(data.sections) ? (data.sections as Array<Record<string, unknown>>) : []
    const gates = sections.map((s) => `${s.gate}:${s.fraction}${s.done ? ' done' : ''}`).join(' ')
    return [
      `${data.lat},${data.lng}`,
      `progress ${data.progress}`,
      data.offTrack ? 'OFF-TRACK' : '',
      `reached ${data.reached}`,
      gates,
      Array.isArray(data.effects) && data.effects.length ? `→ ${(data.effects as string[]).join(',')}` : '',
    ]
      .filter(Boolean)
      .join('  ')
  }

  if (kind === 'ack') {
    return `${data.call} refused — ${data.code ?? 'no code'}: ${data.message ?? 'no message'}`
  }

  if (kind === 'hello') {
    return `device connected — ${data.platform} app ${data.version} (build ${data.build})`
  }

  if (kind === 'heartbeat') {
    // Says the channel is alive when the device has nothing else to report, which is most of the
    // time: every other event comes from the route engine, so an idle phone is silent by design.
    return `still here${data.pending ? ` · ${data.pending} buffered` : ''}`
  }

  if (kind === 'map') {
    return [
      `${data.clusterIn} markers → ${data.clusterOut} clusters`,
      `cluster ${data.clusterMs}ms`,
      `markers ${data.markersMs}ms`,
      `tap ${data.tapMs}ms`,
      `renders ${data.renders}`,
      data.slow ? 'SLOW' : '',
    ]
      .filter(Boolean)
      .join('  ')
  }

  if (kind === 'screen') {
    return `on ${data.path}`
  }

  if (kind === 'http') {
    return `${data.method} ${data.url} → ${data.status ?? 'no response'} ${data.message ?? ''}`.trim()
  }

  if (kind === 'command') {
    // Says whether it landed, not just that it was sent. "done false" with a reason is the answer
    // to somebody pressing a button and watching nothing happen.
    const what = [data.action, data.target, data.delta].filter((v) => v !== undefined).join(' ')
    return data.done
      ? `${what} — done`
      : `${what} — NOT done${data.reason ? `: ${data.reason}` : ''}`
  }

  if (kind === 'frame') {
    if (data.error) return `capture failed — ${data.error}`
    if (data.skipped) return `capture skipped — ${data.skipped}`
    return `screen captured${data.ms ? ` in ${data.ms}ms` : ''}`
  }

  if (kind === 'dropped') {
    return `${data.count} events dropped — the device was buffering faster than it could send`
  }

  return JSON.stringify(data)
}
